/**
 * Task evidence types.
 *
 * Everything the REST endpoints send is an alias into `api.generated.ts`, which is generated from
 * the API's committed `openapi.json` at the commit pinned in `api-pin.json`. A renamed field or a
 * new enum value in the API therefore fails `tsc` here instead of surprising us at runtime.
 *
 * What stays hand-written, and why, is marked below: the error body is rendered by an exception
 * handler rather than a response model, and SSE payloads are outside OpenAPI.
 */
import type { components } from './api.generated';

type Schemas = components['schemas'];

export type EvidenceLifecycleState = Schemas['EvidenceLifecycleState'];
export type EvidenceScanStatus = Schemas['EvidenceScanStatus'];
export type EvidenceAccessAction = Schemas['EvidenceAccessAction'];
export type NotificationHint = Schemas['NotificationHint'];
export type NotificationSeverity = NotificationHint['severity'];
export type EvidenceRecord = Schemas['EvidenceRecord'];
export type EvidenceListResponse = Schemas['EvidenceListResponse'];
export type EvidenceUploadResponse = Schemas['EvidenceUploadResponse'];
export type EvidenceAccessResponse = Schemas['EvidenceAccessResponse'];
export type EvidenceDeleteResponse = Schemas['EvidenceDeleteResponse'];

/**
 * Error body returned by every evidence endpoint on failure. Hand-written: the API renders it from
 * an exception handler (`EvidenceContractError` in `app.main`), so it has no OpenAPI schema.
 */
export interface EvidenceErrorPayload {
  code: string;
  message: string;
  detail?: string | null;
  retryable: boolean;
}

/**
 * SSE event names the backend emits for evidence lifecycle changes. Hand-written: Server-Sent
 * Events are outside OpenAPI. Contract: specs/001-task-evidence-uploads/contracts/evidence-events.md
 * in the API repo (`EvidenceEvent` in `app/models/sse_events.py`).
 */
export const EVIDENCE_SSE_EVENT_TYPES = [
  'evidence.upload_accepted',
  'evidence.upload_failed',
  'evidence.scan_pending',
  'evidence.scan_passed',
  'evidence.scan_failed',
  'evidence.scan_timeout',
  'evidence.delete_requested',
  'evidence.delete_completed',
  'evidence.retention_purge_completed',
  'evidence.quarantine_purge_completed',
  'evidence.access_denied',
  'evidence.access_link_expired',
] as const;

export type EvidenceSseEventType = (typeof EVIDENCE_SSE_EVENT_TYPES)[number];

/** Envelope of every `evidence.*` SSE event (backend `EvidenceEvent`). */
export interface EvidenceSseEvent {
  event_type: EvidenceSseEventType;
  timestamp: string;
  task_id: string;
  evidence_id: string;
  actor_id: string | null;
  notification_code: string;
  message: string;
  severity: NotificationSeverity;
  metadata?: Record<string, unknown> | null;
}
